import * as eventemitter3 from 'eventemitter3';
import { Logger } from './logger.js';
import { ConfigurationSchema } from './model.js';
import { openCirrusSignaling } from './signaling/cirrus.js';
import { openRetailKinesisSignaling } from './signaling/retail-kinesis.js';
import { verifyBrowserSupport } from './utils.js';
import { PlayerHtmlContainers } from './video-streaming/player-html-container.js';
import { establishPeerConnection } from './webrtc/peer-connection.js';
export class WebrtcPlayer extends eventemitter3.EventEmitter {
    playerHtmlContainers;
    dataChannel;
    peerConnection;
    signalingChannel;
    logger;
    state = 'disconnected';
    config;
    reconnectTimer;
    reconnectTryNumber = 0;
    constructor(loggerApi) {
        super();
        this.logger = new Logger(loggerApi);
        this.playerHtmlContainers = PlayerHtmlContainers.setup(this.logger);
        const resetReconnectTryNumber = () => {
            this.reconnectTryNumber = 0;
        };
        this.on('connected', resetReconnectTryNumber);
        this.on('disconnected', resetReconnectTryNumber);
    }
    async connect(configuration) {
        this.throwIfNotInCorrectState();
        const config = ConfigurationSchema.parse(configuration);
        this.logger.info('Connecting to AVE', { config });
        this.config = config;
        this.state = 'connecting';
        this.emit('connecting');
        verifyBrowserSupport();
        try {
            this.signalingChannel = await openSignalingChannel({
                logger: this.logger,
                config
            });
            ({ peerConnection: this.peerConnection, dataChannel: this.dataChannel } = await establishPeerConnection({
                signalingChannel: this.signalingChannel,
                dataChannelOnly: config.dataChannelOnly,
                playerHtmlContainers: this.playerHtmlContainers,
                logger: this.logger
            }));
            this.setupEvents({ signalingType: config.signalingOptions.type });
            if (!config.dataChannelOnly) {
                // HTML elements are only needed for displaying the video
                this.playerHtmlContainers.onUserInteraction = () => this.emit('userInteraction');
                this.playerHtmlContainers.setDataChannel(this.dataChannel);
                this.playerHtmlContainers.addContainer(config);
            }
            this.state = 'connected';
            this.emit('connected');
            this.logger.info('Successfully connected to AVE');
        }
        catch (error) {
            this.logger.error('Error while trying to connect to AVE', { error });
            if (this.config.retryOptions) {
                return this.onConnectionError(error);
            }
            else {
                await this.forceCloseAndCleanStuff();
                this.playerHtmlContainers.close();
                this.config = undefined;
                this.state = 'disconnected';
                throw new Error('Could not connect to AVE', { cause: error });
            }
        }
    }
    throwIfNotInCorrectState() {
        if (this.state === 'connecting') {
            this.logger.warn('Triggered connect while already connecting to AVE');
            throw new Error('Already connecting to AVE');
        }
        if (this.state === 'disconnecting') {
            this.logger.warn('Triggered connect while still disconnecting from AVE');
            throw new Error('Still disconnecting from AVE');
        }
        if (this.state === 'connected') {
            this.logger.warn('Triggered connect while already connected to AVE');
            throw new Error('Already connected to AVE');
        }
    }
    setupEvents(props) {
        this.dataChannel?.on('response', (response) => this.emit('aveMessage', response));
        this.dataChannel?.on('close', () => void this.disconnect('remoteRequest'));
        this.dataChannel?.on('error', (error) => {
            this.logger.debug('Data channel error event fired. This is expected after an AVEEndSessionUpdate', {
                error: new Error('Data channel error event fired', { cause: error })
            });
        });
        this.peerConnection?.addEventListener('connectionstatechange', () => {
            if (['failed'].includes(this.peerConnection?.connectionState ?? '')) {
                void this.onConnectionError(new Error('Peer connection failed'));
            }
        });
        this.signalingChannel?.on('broadcast', (message) => this.emit('cirrusBroadcast', message));
        // In Cirrus case, the reason for closing the connection shall be forwarded to the frontent.
        // It does not matter that we close the entire connection (which we do in the handler).
        // For the Kinesis case, however, we MUST NOT close the connection when the signaling channel closes
        // because it is expected that the signaling connection closes after successful connection.
        // If we do, we also close (and thus break) the connection to the AVE. Bad for the frontends.
        if (props.signalingType === 'cirrus') {
            this.signalingChannel?.on('close', (closeEvent) => void this.disconnect('remoteRequest', closeEvent));
        }
    }
    isConnected() {
        return this.state === 'connected';
    }
    isConnecting() {
        return this.state === 'connecting';
    }
    /**
     * Adds a parent HTML element to the player as an output for the media stream. Does not change the webrtc connection itself.
     * @param props - either `htmlContainerId` or `htmlContainerReference` must be provided.
     * @param props.htmlContainerId - the ID of the HTML element to be added as a parent container.
     * @param props.htmlContainerReference - the HTML element to be added as a parent container.
     * @param props.displayMode - the display mode for the video stream, either 'cover' or 'contain'.
     */
    addHtmlContainer(props) {
        if (this.playerHtmlContainers.containerAlreadyExists(props)) {
            this.logger.info('Cannot add parent as it is already added', { props });
            return;
        }
        this.logger.info('Setting up a new video display on an existing connection', { props });
        this.playerHtmlContainers.addContainer(props);
    }
    /**
     * Removes a parent HTML element from the webrtc player.
     * If the last parent element is removed, the webrtc connection will be closed.
     * @param props - either `htmlContainerId` or `htmlContainerReference` must be provided.
     * @param props.htmlContainerId - the ID of the HTML element to be removed.
     * @param props.htmlContainerReference - the HTML element to be removed.
     * @param props.disconnectOnLastRemoval - if true, the webrtc connection will be closed when the last parent element is removed.
     */
    removeHtmlContainer(props) {
        this.playerHtmlContainers.removeContainer(props);
        if (props.disconnectOnLastRemoval && this.playerHtmlContainers.isEmpty()) {
            this.logger.info('No more parent elements left, disconnecting from AVE');
            void this.disconnect('localRequest');
        }
    }
    async onConnectionError(error) {
        if (this.config?.retryOptions) {
            return this.reconnect(this.config, this.config.retryOptions, error);
        }
        else {
            this.logger.error('Connection error, but no retry options provided, disconnecting', { error });
            await this.disconnect('error');
        }
    }
    async reconnect(config, options, cause) {
        if (this.state === 'reconnecting') {
            this.logger.warn('Already reconnecting to AVE');
            return;
        }
        this.emit('reconnecting');
        this.state = 'reconnecting';
        this.reconnectTryNumber += 1;
        const retryDetails = {
            reconnectTryNumber: this.reconnectTryNumber,
            retryDelayInMs: options.retryDelayInMs,
            cause
        };
        if (this.reconnectTryNumber > options.maxTries) {
            this.logger.error('Connection retries exceeded. Giving up.', retryDetails);
            await this.disconnect('error');
            throw new Error('Connection retries exceeded. Giving up.', { cause });
        }
        this.logger.warn(`Connection error, will try to reconnect in ${options.retryDelayInMs}ms`, retryDetails);
        await this.forceCloseAndCleanStuff();
        const reconnectAfterDelay = await this.waitForRetryDelay(options);
        if (reconnectAfterDelay.wasCancelled) {
            this.logger.info('Reconnect was cancelled');
            this.playerHtmlContainers.close();
        }
        else {
            return this.connect({ ...config });
        }
    }
    async waitForRetryDelay(options) {
        return new Promise((resolve) => {
            this.reconnectTimer = setTimeout(() => resolve({ wasCancelled: false }), options.retryDelayInMs);
            setTimeout(() => resolve({ wasCancelled: true }), options.retryDelayInMs + 10);
        });
    }
    async disconnect(reason = 'localRequest', closeEvent) {
        if (this.state === 'connecting') {
            this.logger.warn('Still connecting to AVE, waiting for connection to be established before disconnecting');
            await new Promise((resolve) => {
                this.once('connected', () => resolve());
                this.once('reconnecting', () => resolve());
                this.once('disconnected', () => resolve());
            });
        }
        if (this.state === 'reconnecting') {
            clearTimeout(this.reconnectTimer);
        }
        if (this.state === 'disconnected') {
            this.logger.warn('Not connected to AVE');
            return;
        }
        if (this.state === 'disconnecting') {
            this.logger.warn('Already disconnecting from AVE');
            return new Promise((resolve) => this.once('disconnected', () => resolve(undefined)));
        }
        this.state = 'disconnecting';
        this.emit('disconnecting');
        await this.forceCloseAndCleanStuff();
        this.playerHtmlContainers.close();
        this.config = undefined;
        this.state = 'disconnected';
        this.emit('disconnected', reason, closeEvent);
        this.logger.info('Disconnected from AVE', { reason, closeEvent });
    }
    async forceCloseAndCleanStuff() {
        this.dataChannel?.removeAllListeners();
        this.cleanUpPeerConnectionEventHandlers();
        this.peerConnection?.close();
        try {
            await this.dataChannel?.closeAsync(); // Closing the data channel after the peer connection doesn't work for Webstreaming 🤷
        }
        catch (error) {
            this.logger.warn('Error while trying to close data channel', { error });
        }
        this.peerConnection = undefined;
        this.dataChannel = undefined;
        try {
            await this.signalingChannel?.closeAsync();
        }
        catch (error) {
            this.logger.warn('Error while trying to close signaling channel', { error });
        }
        this.signalingChannel?.removeAllListeners();
        this.signalingChannel = undefined;
    }
    cleanUpPeerConnectionEventHandlers() {
        if (this.peerConnection) {
            this.peerConnection.onconnectionstatechange = null;
            this.peerConnection.onicecandidate = null;
            this.peerConnection.onicecandidateerror = null;
            this.peerConnection.oniceconnectionstatechange = null;
            this.peerConnection.onicegatheringstatechange = null;
            this.peerConnection.onnegotiationneeded = null;
            this.peerConnection.onsignalingstatechange = null;
            this.peerConnection.ontrack = null;
        }
    }
    /**
     * Sends `request` to the connected AVE. Does nothing if no connection to an AVE exists.
     * The request should usually be generated via the {@link @org-avp/ave-message-factory}.
     * Responses are sent asynchronously by the AVE and can be received by listenting to the `aveMessage` event.
     * @param request a request to be sent to the connected AVE.
     */
    sendAveRequest(request) {
        this.dataChannel?.sendAveCommand(request);
    }
    /**
     * Sends a control message to a cloud streaming server.
     * The message should usually be generated via the {@link @org-avp/cirrus-message-factory}.
     * This requests will only be executed, when connected to a cloud streaming instance instead of a local retail AVE.
     * @param request a request to be sent to the connected cloud streaming control and signaling server.
     */
    sendCirrusRequest(request) {
        this.signalingChannel?.send(request);
    }
    /**
     * @param message message to be sent
     * @deprecated Please use {@link WebrtcPlayer.sendAveRequest} instead
     */
    sendDatachannelMessage(message) {
        this.dataChannel?.sendAveCommand(message);
    }
    /**
     * @param message message to be sent
     * @deprecated Please use {@link WebrtcPlayer.sendCirrusRequest} instead
     */
    sendSignalingMessage(message) {
        this.signalingChannel?.send(message);
    }
}
async function openSignalingChannel(props) {
    const signalingOptions = props.config.signalingOptions;
    if (signalingOptions.type === 'aws') {
        return await openRetailKinesisSignaling({
            logger: props.logger,
            awsSignalingOptions: signalingOptions.aws
        });
    }
    else {
        return await openCirrusSignaling({
            logger: props.logger,
            cirrusSignalingOptions: signalingOptions.cirrus
        });
    }
}
//# sourceMappingURL=webrtc-player.js.map